import { html } from '../../mixins/katapult-element';
import { KatapultEditorTemplate } from './katapult-editor-template';
import { GetTensionTable } from '../../modules/GetTensionTable.js';
import { CamelCase } from '../../modules/CamelCase.js';
import '../katapult-switcher/katapult-switcher.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import '../katapult-elements/katapult-button.js';
import '../input-element/input-element.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';
import { FirebaseEncode } from '../../modules/FirebaseEncode.js';

class KatapultWireSpecEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        :host {
          --paper-input-container-disabled: {
            opacity: 0.7;
          };
        }
        paper-row[disabled] {
          opacity: 0.7;
          pointer-events: none;
        }
        paper-tabs {
          --paper-tabs-selection-bar-color: var(--secondary-color);
          --paper-tab-ink: var(--secondary-color);
          --paper-tabs-content: {
            display: flex;
            min-width: 100%;
          };
        }
        paper-tab {
          --paper-tab: {
            font-size: 12pt;
            font-weight: normal;
          };
          --paper-tab-content-focused: {
            font-weight: normal;
          };
        }
        #specSourceLabel {
          position: absolute;
          top: 0;
          right: 0;
          padding: 1.25em 1em;
          font-style: italic;
          color: var(--primary-text-color-faded);
          background: white;
        }
      </style>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes/wire_spec" data="{{wireSpecAttribute}}">
      </katapult-firebase-worker>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/pole_loading/tension_districts" data="{{tensionDistricts}}">
      </katapult-firebase-worker>

      <template is="dom-if" if="[[itemData.source]]">
        <div id="specSourceLabel">Source: [[itemData.source]]</div>
      </template>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <!-- Weight -->
          <template is="dom-if" if="[[equal(step.id, 'weight')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Weight"
                    value="[[_itemData.weight]]"
                    data-path="_itemData.weight"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">lbf/ft</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Tension Table Calculations -->
          <template is="dom-if" if="[[equal(step.id, 'tension_table_calculations')]]" restamp>
            <katapult-switcher
              slot="body"
              items="[[tensionTableCalculationTypes]]"
              label-path="label"
              value-path="value"
              value="[[_itemData.tension_table_calculations]]"
              data-path="_itemData.tension_table_calculations"
              on-value-changed="updateProperty"
              on-input="tensionTableCalculationsChanged"
              disabled="[[!fullyEditable]]"
            ></katapult-switcher>
          </template>

          <!-- Slack Tension -->
          <template is="dom-if" if="[[equal(step.id, 'tension_slack')]]" restamp>
            <div style="display:flex; flex-direction:column;" slot="body">
              <paper-table style="width: 700px;" rounded outline dividers overflow-hidden>
                <paper-row grey divider>
                  <paper-tabs style="flex-grow: 1;" selected="{{selectedTensionTab}}" attr-for-selected="name">
                    <paper-tab name="_baseline">Baseline</paper-tab>
                    <template is="dom-repeat" items="[[toArray(tensionDistricts)]]">
                      <paper-tab name$="[[item.$key]]">[[CamelCase(item.$key)]]</paper-tab>
                    </template>
                  </paper-tabs>
                </paper-row>
                <div style="display:flex; padding:2em; gap:2em;">
                  <!-- Environmental Properties of Load Case / District -->
                  <paper-table style="width: 50%;" rounded outline dividers overflow-hidden>
                    <paper-row light-grey short><paper-cell>Environment</paper-cell></paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Temperature"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'slack', 'temperature')]]"
                          data-path$="_itemData.tension_tables.slack.[[selectedTensionTab]].properties.temperature"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">°F</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Ice"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'slack', 'ice')]]"
                          data-path$="_itemData.tension_tables.slack.[[selectedTensionTab]].properties.ice"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">in</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Wind"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'slack', 'wind')]]"
                          data-path$="_itemData.tension_tables.slack.[[selectedTensionTab]].properties.wind"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">lbf/ft²</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Constant"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'slack', 'conductor_constant')]]"
                          data-path$="_itemData.tension_tables.slack.[[selectedTensionTab]].properties.conductor_constant"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">lbf/ft</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                  <!-- Tension Table -->
                  <paper-table style="width: 50%;" rounded outline dividers overflow-hidden>
                    <paper-row light-grey short>
                      <paper-cell>Span Length</paper-cell>
                      <paper-cell>Tension</paper-cell>
                      <paper-cell icon></paper-cell>
                    </paper-row>
                    <template is="dom-repeat" items="[[getTable(tension.slack, selectedTensionTab, tension.slack.*)]]" mutable-data>
                      <paper-row>
                        <paper-cell>
                          <paper-input
                            label="Span Length"
                            value="[[item.$key]]"
                            data-path$="tension.slack.[[selectedTensionTab]].$(model.index).$key"
                            on-change="updateEncodedProperty"
                            no-label-float
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          >
                            <span slot="suffix">ft</span>
                          </paper-input>
                        </paper-cell>
                        <paper-cell>
                          <paper-input
                            label="Tension"
                            value="[[item.$val]]"
                            data-path$="tension.slack.[[selectedTensionTab]].$(model.index).$val"
                            on-value-changed="updateProperty"
                            no-label-float
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          >
                            <span slot="suffix">lbf</span>
                          </paper-input>
                        </paper-cell>
                        <paper-cell icon-button>
                          <katapult-button
                            iconOnly
                            noBorder
                            icon="delete"
                            data-type="slack"
                            on-click="removeTension"
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          ></katapult-button>
                        </paper-cell>
                      </paper-row>
                    </template>
                    <!-- Add Tension -->
                    <paper-row
                      light-grey
                      short
                      pointer
                      ripple
                      data-type="slack"
                      on-click="addTension"
                      disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                    >
                      <paper-cell center style="gap: 8px;">
                        <material-icon icon="playlist_add"></material-icon>
                        <span>Add Slack Tension</span>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                </div>
              </paper-table>
            </div>
            <katapult-button style="margin-left: 16px;" slot="current-step-header" on-click="nextStep">Continue</katapult-button>
          </template>
          <!-- Full Tension -->
          <template is="dom-if" if="[[equal(step.id, 'tension_full')]]" restamp>
            <div style="display:flex; flex-direction:column;" slot="body">
              <paper-table style="min-width: 500px;" rounded outline dividers overflow-hidden>
                <paper-row grey divider>
                  <paper-tabs style="flex-grow: 1;" selected="{{selectedTensionTab}}" attr-for-selected="name">
                    <paper-tab name="_baseline">Baseline</paper-tab>
                    <template is="dom-repeat" items="[[toArray(tensionDistricts)]]">
                      <paper-tab name$="[[item.$key]]">[[CamelCase(item.$key)]]</paper-tab>
                    </template>
                  </paper-tabs>
                </paper-row>
                <div style="display:flex; padding:2em; gap:2em;">
                  <!-- Environmental Properties of Load Case / District -->
                  <paper-table style="width: 50%;" rounded outline dividers overflow-hidden>
                    <paper-row light-grey short><paper-cell>Environment</paper-cell></paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Temperature"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'full', 'temperature')]]"
                          data-path$="_itemData.tension_tables.full.[[selectedTensionTab]].properties.temperature"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">°F</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Ice"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'full', 'ice')]]"
                          data-path$="_itemData.tension_tables.full.[[selectedTensionTab]].properties.ice"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">in</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Wind"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'full', 'wind')]]"
                          data-path$="_itemData.tension_tables.full.[[selectedTensionTab]].properties.wind"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">lbf/ft²</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                    <paper-row>
                      <paper-cell>
                        <paper-input
                          label="Constant"
                          value="[[getEnvironmentProperty(selectedTensionTab, 'full', 'conductor_constant')]]"
                          data-path$="_itemData.tension_tables.full.[[selectedTensionTab]].properties.conductor_constant"
                          on-value-changed="updateProperty"
                          disabled="[[!equal(selectedTensionTab, '_baseline')]]"
                        >
                          <span slot="suffix">lbf/ft</span>
                        </paper-input>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                  <!-- Tension Table -->
                  <paper-table style="width: 50%;" rounded outline dividers overflow-hidden>
                    <paper-row light-grey short>
                      <paper-cell>Span Length</paper-cell>
                      <paper-cell>Tension</paper-cell>
                      <paper-cell icon></paper-cell>
                    </paper-row>
                    <template is="dom-repeat" items="[[getTable(tension.full, selectedTensionTab, tension.full.*)]]" mutable-data>
                      <paper-row>
                        <paper-cell>
                          <paper-input
                            label="Span Length"
                            value="[[item.$key]]"
                            data-path$="tension.full.[[selectedTensionTab]].$(model.index).$key"
                            on-change="updateEncodedProperty"
                            no-label-float
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          >
                            <span slot="suffix">ft</span>
                          </paper-input>
                        </paper-cell>
                        <paper-cell>
                          <paper-input
                            label="Tension"
                            value="[[item.$val]]"
                            data-path$="tension.full.[[selectedTensionTab]].$(model.index).$val"
                            on-value-changed="updateProperty"
                            no-label-float
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          >
                            <span slot="suffix">lbf</span>
                          </paper-input>
                        </paper-cell>
                        <paper-cell icon-button>
                          <katapult-button
                            iconOnly
                            noBorder
                            icon="delete"
                            data-type="full"
                            on-click="removeTension"
                            disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                          ></katapult-button>
                        </paper-cell>
                      </paper-row>
                    </template>
                    <!-- Add Tension -->
                    <paper-row
                      light-grey
                      short
                      pointer
                      ripple
                      data-type="full"
                      on-click="addTension"
                      disabled$="[[tensionDisabled(selectedTensionTab, _itemData.tension_table_calculations)]]"
                    >
                      <paper-cell center style="gap: 8px;">
                        <material-icon icon="playlist_add"></material-icon>
                        <span>Add Full Tension</span>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                </div>
              </paper-table>
            </div>
            <katapult-button style="margin-left: 16px;" slot="current-step-header" on-click="nextStep">Continue</katapult-button>
          </template>
          <!-- Wire Spec -->
          <template is="dom-if" if="[[equal(step.id, 'wire_spec')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <input-element
                    label="Wire Spec"
                    attribute_name="wire_spec"
                    hide-attribute-name
                    value="[[_itemData.wire_spec]]"
                    data-path="_itemData.wire_spec"
                    on-value-changed="updateProperty"
                    model="[[wireSpecAttribute]]"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  ></input-element>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Diameter -->
          <template is="dom-if" if="[[equal(step.id, 'diameter')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Diameter"
                    value="[[_itemData.diameter]]"
                    data-path="_itemData.diameter"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">in</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
          <!-- Cross Sectional Area -->
          <template is="dom-if" if="[[equal(step.id, 'cross_sectional_area')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Cross Sectional Area"
                    value="[[_itemData.cross_sectional_area]]"
                    data-path="_itemData.cross_sectional_area"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">in²</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
          <!-- Modulus of Elasticity -->
          <template is="dom-if" if="[[equal(step.id, 'modulus_of_elasticity')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Modulus of Elasticity"
                    value="[[_itemData.modulus_of_elasticity]]"
                    data-path="_itemData.modulus_of_elasticity"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">psi</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
          <!-- Expansion Coefficient -->
          <template is="dom-if" if="[[equal(step.id, 'expansion_coefficient')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Expansion Coefficient"
                    value="[[_itemData.expansion_coefficient]]"
                    data-path="_itemData.expansion_coefficient"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">°F⁻¹</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
          <!-- Rated Breaking Strength -->
          <template is="dom-if" if="[[equal(step.id, 'rated_breaking_strength')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Rated Breaking Strength"
                    value="[[_itemData.rated_breaking_strength]]"
                    data-path="_itemData.rated_breaking_strength"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">lbf</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
          <!-- Bundled Components -->
          <template is="dom-if" if="[[equal(step.id, 'bundled_components')]]" restamp>
            <paper-table rounded outline dividers overflow-hidden slot="body" style="min-width: 500px;">
              <paper-row grey short>
                <paper-cell style="gap: 8px;">
                  <div>Name</div>
                </paper-cell>
                <paper-cell style="gap: 8px;">
                  <katapult-icon icon="circle" outline></katapult-icon>
                  <div>Diameter</div>
                </paper-cell>
                <paper-cell style="gap: 8px;">
                  <katapult-icon icon="line_weight"></katapult-icon>
                  <div>Weight</div>
                </paper-cell>
                <paper-cell icon></paper-cell>
              </paper-row>
              <template is="dom-repeat" items="[[getItemsArray(_itemData.bundled_components.*)]]" mutable-data>
                <paper-row>
                  <paper-cell input>
                    <paper-input
                      label="Name"
                      value="[[item.name]]"
                      data-path="_itemData.bundled_components.$(model.item.$key).name"
                      on-value-changed="updateProperty"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    ></paper-input>
                  </paper-cell>
                  <paper-cell input>
                    <paper-input
                      label="Diameter"
                      value="[[item.diameter]]"
                      data-path="_itemData.bundled_components.$(model.item.$key).diameter"
                      on-value-changed="updateProperty"
                      data-parse="float"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    >
                      <span slot="suffix">in</span>
                    </paper-input>
                  </paper-cell>
                  <paper-cell input>
                    <paper-input
                      label="Weight"
                      value="[[item.weight]]"
                      data-path="_itemData.bundled_components.$(model.item.$key).weight"
                      on-value-changed="updateProperty"
                      data-parse="float"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    >
                      <span slot="suffix">lbf/ft</span>
                    </paper-input>
                  </paper-cell>
                  <paper-cell icon-button>
                    <katapult-button
                      iconOnly
                      noBorder
                      icon="delete"
                      data-path="_itemData.bundled_components.$(model.item.$key)"
                      data-value="$null"
                      on-click="updateProperty"
                    ></katapult-button>
                  </paper-cell>
                </paper-row>
              </template>
              <paper-row ripple light-grey short pointer on-click="addBundledComponent">
                <paper-cell style="gap: 8px;" justify-center>
                  <material-icon icon="add_circle"></material-icon>
                  <div>Add Bundled Component</div>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      selectedTensionTab: {
        type: String,
        value: '_baseline'
      },
      tensionTableCalculationTypes: {
        type: Array,
        value: () => [
          { value: 'calculated', label: 'Calculated from Baseline' },
          { value: 'manual', label: 'Manually Entered' }
        ]
      }
    };
  }

  static get observers() {
    return ['tensionChanged(tension.*)'];
  }

  constructor() {
    super();
    this.computedLabel = {
      dependencies: [{ path: 'wire_spec' }],
      compute: (spec) => spec ?? 'Untitled Wire Spec'
    };
    this.CamelCase = CamelCase;
  }

  ready() {
    super.ready();

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'wire_spec',
        title: 'Wire Spec',
        trigger: () => this.get('_itemData.wire_spec') != null
      },
      {
        id: 'weight',
        title: 'Weight',
        trigger: () => this.get('_itemData.weight') != null
      },
      {
        id: 'tension_table_calculations',
        title: 'Tension Table Calculations',
        trigger: () => this.get('_itemData.tension_table_calculations') != null
      },
      {
        id: 'tension_slack',
        title: 'Slack Horizontal Tension',
        trigger: () => this.get('_itemData.tension_tables.slack') != null
      },
      {
        id: 'tension_full',
        title: 'Full Horizontal Tension',
        trigger: () => this.get('_itemData.tension_tables.full') != null
      },
      {
        id: 'diameter',
        title: 'Diameter',
        trigger: () => this.get('_itemData.diameter') != null
      },
      {
        id: 'cross_sectional_area',
        title: 'Cross Sectional Area',
        trigger: () => this.get('_itemData.cross_sectional_area') != null
      },
      {
        id: 'modulus_of_elasticity',
        title: 'Modulus of Elasticity',
        trigger: () => this.get('_itemData.modulus_of_elasticity') != null
      },
      {
        id: 'expansion_coefficient',
        title: 'Expansion Coefficient',
        trigger: () => this.get('_itemData.expansion_coefficient') != null
      },
      {
        id: 'rated_breaking_strength',
        title: 'Rated Breaking Strength',
        trigger: () => this.get('_itemData.rated_breaking_strength') != null,
        if: () => this.isGuyWire()
      },
      {
        id: 'bundled_components',
        title: 'Bundled Components (Optional)',
        trigger: () => this.get('_itemData.bundled_components') != null
      }
    ]);
  }

  addTension(e) {
    this.push(`tension.${e.currentTarget.dataset.type}.${this.selectedTensionTab}`, {});
  }

  removeTension(e) {
    this.splice(`tension.${e.currentTarget.dataset.type}.${this.selectedTensionTab}`, e.model.index, 1);
  }

  filterTension(key) {
    return !key.startsWith('_');
  }

  getTable(tensions, table) {
    if (tensions?.[table]) {
      // Decode the Firebase keys to be used as the span length. This is needed because we allow span lengths to be decimals
      for (let i = 0; i < tensions[table].length; i++) {
        // Skip any keys that are already numbers
        if (typeof tensions[table][i].$key == 'number') continue;

        let key = FirebaseEncode.decode(tensions[table][i].$key);
        tensions[table][i].$key = key;
      }

      // Sort the table by span length
      tensions[table].sort((a, b) => a.$key - b.$key);
    }
    return tensions?.[table];
  }

  updateEncodedProperty(e) {
    const value = e.currentTarget.value.toString();
    e.detail.value = FirebaseEncode.encode(value);
    this.updateProperty(e);
  }

  addBundledComponent(e) {
    this.deepSet(`_itemData.bundled_components.${GeneratePushID()}`, { name: '', diameter: '', weight: '' });
  }

  isGuyWire() {
    const guys = this.wireSpecAttribute?.picklists?.guy_specs ?? [];
    return guys.find((x) => x.value == this._itemData?.wire_spec) != null;
  }

  setInternalItemData(...args) {
    super.setInternalItemData(...args);
    // Build the local array-based data structure "tension" to be used be the interface.
    setTimeout(() => {
      let tension = {
        slack: {},
        full: {}
      };
      let firstWithData = null;
      ['slack', 'full'].forEach((type) => {
        for (let key in this._itemData?.tension_tables?.[type]) {
          let table = this._itemData.tension_tables[type][key];
          if (key == '_baseline') table = table.tensions;
          if (table) delete table.$key;
          let tableList = this.toArray(table);
          if (tableList.some((x) => x.$val != null && x.$val != '') && !firstWithData) firstWithData = key;
          tension[type][key] = tableList;
        }
      });
      this.tension = tension;
      if (firstWithData && !this.selectedTensionTabSet) {
        this.selectedTensionTabSet = true;
        this.selectedTensionTab = firstWithData;
      }
    });
  }

  tensionChanged() {
    // Update _itemData with the new changes to "tension" the local data structure.
    if (this._itemData?.tension_tables) {
      const update = {};
      ['full', 'slack'].forEach((type) => {
        for (let district in this._itemData.tension_tables[type]) {
          let path = `_itemData.tension_tables.${type}.${district}${district == '_baseline' ? '.tensions' : ''}`;
          // "Delete" all existing tensions.
          Object.keys(this.get(path) ?? {})
            .filter(this.filterTension)
            .forEach((key) => {
              update[`${path}.${key}`] = null;
            });

          // Add in any that exist in the array version.
          this.tension[type][district]
            ?.filter((x) => x.$key != null && x.$key != '')
            .forEach((tension) => {
              const parsedValue = parseFloat(tension.$val);
              update[`${path}.${FirebaseEncode.encode(tension.$key)}`] = !isNaN(parsedValue) ? parsedValue : null;
            });
        }
      });
      this.setProperties(update);
    }
  }

  tensionTableCalculationsChanged() {
    // Confirm that we have all the data we need
    let missingKeys = ['diameter', 'weight', 'cross_sectional_area', 'modulus_of_elasticity', 'expansion_coefficient']
      .filter((x) => this.get(`_itemData.${x}`) == null || this.get(`_itemData.${x}`) == '')
      .map(CamelCase);
    // And that there are some baseline slack tensions (assuming we have any slack tensions at all)
    let slackTensions = Object.values(this.get(`_itemData.tension_tables.slack._baseline.tensions`) ?? {}).filter(
      (x) => x != null && x != ''
    ).length;
    let existingSlackTensions = 0;
    for (let key in this._itemData?.tension_tables?.slack) {
      if (key == '_baseline') continue;
      existingSlackTensions += Object.values(this._itemData.tension_tables.slack[key]).filter((x) => x != null && x != '');
    }
    if (!slackTensions && existingSlackTensions.length) missingKeys.push('Slack Baseline Tensions');
    // And that there are baseline full tensions
    let fullTensions = Object.values(this.get(`_itemData.tension_tables.full._baseline.tensions`) ?? {}).filter(
      (x) => x != null && x != ''
    ).length;
    if (!fullTensions) missingKeys.push('Full Baseline Tensions');
    // If there is missing data and they are trying to switch to calculated, alert the user and switch back to manual
    if (this._itemData?.tension_table_calculations == 'calculated' && missingKeys.length > 0) {
      let lastItem = missingKeys.pop();
      KatapultDialog.alert({
        body: `${missingKeys.join(', ') + (missingKeys.length > 0 ? ` and ${lastItem}` : lastItem)} ${
          missingKeys.length > 0 ? 'are' : 'is'
        } missing from the wire definition and ${missingKeys.length > 0 ? 'are' : 'is'} required for calculated tension tables.`,
        dialog: { title: 'Missing Required Data to Calculate Tensions', icon: 'warning', color: 'var(--paper-red-500)' },
        button: { label: 'Okay' }
      });
      this.set(`_itemData.tension_table_calculations`, 'manual');
    }
    // Assuming there were no errors, communicate with the user what it means to switch between calculated and manual tensions
    else {
      let body =
        this._itemData?.tension_table_calculations == 'calculated'
          ? 'Calculated Tensions take a user entered baseline and apply the correct Environmental Properties to calculated the correct tensions for each loading district.'
          : 'Manual Tension tables are user-configured, and should contain full horizontal tensions, including all Environmental Properties for the given loading district.';
      KatapultDialog.alert({
        body,
        dialog: { title: 'Tension Calculations', icon: 'calculate' },
        button: { label: 'Okay', icon: 'check' }
      });
    }
  }

  _itemDataChanged(changeRecord) {
    super._itemDataChanged(changeRecord);
    // Build out the tension table if any pieces are missing.
    if (this._itemData) {
      this._itemData.tension_tables ??= {};
      ['full', 'slack'].forEach((type) => {
        this._itemData.tension_tables[type] ??= {};
        this._itemData.tension_tables[type]._baseline ??= { properties: {}, tensions: {} };
        for (let key in this.tensionDistricts) {
          this._itemData.tension_tables[type][key] ??= {};
        }
      });
    }
  }

  beforeSave() {
    // If we are calculating tensions, do that before saving.
    if (this._itemData.tension_table_calculations == 'calculated') {
      // Get the properties of the cable.
      let cableProperties = {
        diameter: this._itemData.diameter,
        linearWeight: this._itemData.weight,
        crossSectionalArea: this._itemData.cross_sectional_area,
        youngsMod: this._itemData.modulus_of_elasticity,
        thermalExpansionCoeff: this._itemData.expansion_coefficient,
        bundleComponents: this._itemData.bundled_components
      };
      // Get the baseline tensions.
      let baselineTensions = {};
      for (let key in this._itemData?.tension_tables) {
        baselineTensions[key] = this._itemData.tension_tables[key]._baseline;
        for (let rulingSpan in baselineTensions[key].tensions) {
          let tension = baselineTensions[key].tensions[rulingSpan];
          if (rulingSpan == null || isNaN(rulingSpan) || tension == null || isNaN(tension)) {
            delete baselineTensions[key].tensions[rulingSpan];
          }
        }
      }
      // Calculate the tension tables.
      this._itemData.tension_tables = GetTensionTable(baselineTensions, cableProperties, this.tensionDistricts);
    }
    this._itemData.source = `Manual Entry - ${katapultAuth.user.email} (${new Date().toLocaleDateString()})`;
  }

  tensionDisabled(selectedTensionTab, tension_table_calculations) {
    // Disable tension editing of all the tables except the baseline if we are calculating tensions.
    return tension_table_calculations == 'calculated' && selectedTensionTab != '_baseline';
  }

  getEnvironmentProperty(selectedTensionTab, type, property) {
    // Get the user-entered baseline properties
    if (selectedTensionTab == '_baseline') {
      return this.get(`_itemData.tension_tables.${type}.${selectedTensionTab}.properties.${property}`);
    }
    // Or the standard district properties
    else {
      let keys = {
        wind: 'horizontal_wind_pressure',
        temperature: 'analysis_temp',
        conductor_constant: 'conductor_constant',
        ice: 'radial_ice_thickness'
      };
      return this.get(`tensionDistricts.${selectedTensionTab}.${keys[property]}`);
    }
  }
}

customElements.define('katapult-wire-spec-editor', KatapultWireSpecEditor);
